import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Loader2, AlertCircle, CheckCircle, Sun, Moon, FileCheck, ShieldCheck, KeyRound, ArrowLeft } from 'lucide-react';
import { useAuth } from '../stores/authStore';
import { useTheme } from '../stores/themeStore';
import { authApi } from '../services/api';

const loginSchema = z.object({
  email: z.string().email('Email không hợp lệ'),
});

type LoginForm = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { login, verifyMagicLink, verify2fa, initialized } = useAuth();
  const [step, setStep] = useState<'email' | 'verify' | '2fa'>('email');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [devLink, setDevLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [tempToken2fa, setTempToken2fa] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const autoVerifyStarted = useRef(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = async (data: LoginForm) => {
    setError(null);
    setLoading(true);
    try {
      const result = await login(data.email);

      // If user has 2FA enabled, immediately switch to 2FA input
      if (result && (result as any).requires_2fa) {
        setTempToken2fa((result as any).temp_token);
        setStep('2fa');
        setError(null);
        setSuccess(null);
        return;
      }

      let token = (result as any)?.token;
      if (!token && result?.devLink) {
        try {
          const url = new URL(result.devLink);
          token = url.searchParams.get('token');
        } catch {
          // ignore
        }
      }

      if (token) {
        setSuccess('Đang xác thực thông tin tài khoản...');
        const res = await verifyMagicLink(token, 'login');
        if (res && (res as any).requires_2fa) {
          setTempToken2fa((res as any).temp_token);
          setStep('2fa');
          setError(null);
          setSuccess(null);
          return;
        }
        setSuccess('Đăng nhập thành công! Đang chuyển hướng...');
        navigate('/dashboard', { replace: true });
        return;
      }

      setMagicLinkSent(true);
      setStep('verify');
      setSuccess('Đã gửi liên kết đăng nhập đến email của bạn. Vui lòng kiểm tra hộp thư.');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Có lỗi xảy ra. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  const verifySchema = z.object({
    token: z.string().min(1, 'Vui lòng nhập token'),
  });

  type VerifyForm = z.infer<typeof verifySchema>;

  const {
    register: registerVerify,
    handleSubmit: handleSubmitVerify,
    formState: { errors: verifyErrors },
  } = useForm<VerifyForm>({
    resolver: zodResolver(verifySchema),
  });

  const onVerifySubmit = async (data: VerifyForm) => {
    setError(null);
    setLoading(true);
    try {
      if (!data.token) {
        throw new Error('Vui lòng nhập mã xác thực');
      }
      const res = await verifyMagicLink(data.token, 'login');
      if (res && (res as any).requires_2fa) {
        setTempToken2fa((res as any).temp_token);
        setStep('2fa');
        setSuccess(null);
        return;
      }
      setSuccess('Đăng nhập thành công!');
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Liên kết không hợp lệ hoặc đã hết hạn');
    } finally {
      setLoading(false);
    }
  };

  const handle2faSubmit = async (e?: React.FormEvent, customCode?: string) => {
    if (e) e.preventDefault();
    const codeToUse = (customCode !== undefined ? customCode : totpCode).trim();
    if (!tempToken2fa || !codeToUse) return;
    setError(null);
    setLoading(true);
    try {
      await verify2fa(tempToken2fa, codeToUse);
      setSuccess('Xác thực 2 bước thành công! Đang chuyển hướng...');
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 100);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Mã xác thực 2FA không chính xác hoặc đã hết hạn');
    } finally {
      setLoading(false);
    }
  };

  // Auto-verify when the user arrives from the magic-link email
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    if (!token || !initialized || autoVerifyStarted.current) return;

    autoVerifyStarted.current = true;
    // Strip token from browser address bar immediately so re-renders or page refreshes don't re-execute
    window.history.replaceState({}, document.title, window.location.pathname);

    setStep('verify');
    setLoading(true);
    verifyMagicLink(token, params.get('purpose') || 'login')
      .then((res) => {
        if (res && (res as any).requires_2fa) {
          setTempToken2fa((res as any).temp_token);
          setStep('2fa');
          setSuccess(null);
          return;
        }
        setSuccess('Đăng nhập thành công! Đang chuyển hướng...');
        navigate('/dashboard', { replace: true });
      })
      .catch((err: any) => {
        setError(err.response?.data?.error?.message || 'Liên kết không hợp lệ hoặc đã hết hạn');
      })
      .finally(() => setLoading(false));
  }, [location.search, initialized, navigate, verifyMagicLink]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-[#0b0f19] text-slate-900 dark:text-slate-100 px-4 transition-colors relative">
      {/* Top right theme toggle */}
      <div className="absolute top-4 right-4">
        <button
          onClick={toggleTheme}
          className="p-2.5 rounded-xl text-slate-500 dark:text-slate-400 hover:bg-slate-200/60 dark:hover:bg-slate-800 border border-slate-200 dark:border-white/10 transition-all"
          title={theme === 'dark' ? 'Chuyển sang giao diện Sáng' : 'Chuyển sang giao diện Tối'}
        >
          {theme === 'dark' ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-slate-600" />}
        </button>
      </div>

      <div className="w-full max-w-md py-8">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2.5 mb-3">
            <div className="w-11 h-11 bg-gradient-to-br from-sky-400 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-sky-500/20 text-white">
              <FileCheck className="w-6 h-6" />
            </div>
            <span className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">PDF Sign</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            {step === 'email' ? 'Đăng nhập hệ thống' : step === '2fa' ? 'Xác thực 2 lớp (2FA)' : 'Xác thực tài khoản'}
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5">
            {step === 'email'
              ? 'Nền tảng ký điện tử & phân luồng văn bản đám mây'
              : step === '2fa'
              ? 'Nhập mã 6 chữ số từ ứng dụng Google Authenticator hoặc mã dự phòng'
              : 'Nhập mã xác thực hoặc nhấn liên kết đăng nhập'}
          </p>
        </div>

        {/* Card */}
        <div className="card p-6 sm:p-8 border border-slate-200/80 dark:border-white/10 shadow-xl shadow-slate-200/50 dark:shadow-none bg-white dark:bg-[#111827]">
          {error && (
            <div className="mb-6 p-3.5 bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-500/20 rounded-xl flex items-center gap-3 text-rose-700 dark:text-rose-300 text-xs">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 p-3.5 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-500/20 rounded-xl flex items-center gap-3 text-emerald-700 dark:text-emerald-300 text-xs">
              <CheckCircle className="w-4 h-4 flex-shrink-0" />
              <p>{success}</p>
            </div>
          )}

          {step === 'email' ? (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div>
                <label htmlFor="email" className="label">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    {...register('email')}
                    id="email"
                    type="email"
                    autoComplete="email"
                    className={`input pl-10 ${errors.email ? 'input-error' : ''}`}
                    placeholder="nguyen.van.a@company.com"
                    disabled={loading}
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-danger-600">{errors.email.message}</p>
                )}
              </div>

              <button
                type="submit"
                className="btn-primary w-full"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Đang đăng nhập...
                  </>
                ) : (
                  'Đăng nhập'
                )}
              </button>
            </form>
          ) : step === '2fa' ? (
            <form onSubmit={handle2faSubmit} className="space-y-5">
              <div className="p-3.5 bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-500/20 rounded-xl flex items-center gap-3 text-sky-800 dark:text-sky-300 text-xs">
                <ShieldCheck className="w-5 h-5 flex-shrink-0 text-sky-500" />
                <p>Tài khoản của bạn đã được bảo vệ bởi xác thực hai bước TOTP. Mở ứng dụng xác thực trên điện thoại để lấy mã.</p>
              </div>

              <div>
                <label htmlFor="totpCode" className="label flex items-center justify-between">
                  <span>Mã xác thực 6 chữ số / Mã dự phòng</span>
                  <KeyRound className="w-4 h-4 text-slate-400" />
                </label>
                <input
                  id="totpCode"
                  type="text"
                  autoFocus
                  maxLength={12}
                  value={totpCode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\s+/g, '');
                    setTotpCode(val);
                    if (val.length === 6 && /^\d{6}$/.test(val)) {
                      handle2faSubmit(undefined, val);
                    }
                  }}
                  className="input tracking-widest text-center text-xl font-mono"
                  placeholder="000000"
                  disabled={loading}
                />
                <p className="text-[11px] text-slate-400 mt-1.5 text-center">
                  Hỗ trợ cả mã 6 số từ Google Authenticator hoặc mã dự phòng 8 ký tự
                </p>
              </div>

              <button
                type="submit"
                className="btn-primary w-full"
                disabled={loading || !totpCode.trim()}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Đang kiểm tra mã 2FA...
                  </>
                ) : (
                  'Xác thực và Đăng nhập'
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setStep('email');
                  setTotpCode('');
                  setTempToken2fa(null);
                  setError(null);
                  setSuccess(null);
                }}
                className="btn-ghost w-full flex items-center justify-center gap-1.5 text-xs"
                disabled={loading}
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Quay lại màn hình đăng nhập
              </button>
            </form>
          ) : (
            <form onSubmit={handleSubmitVerify(onVerifySubmit)} className="space-y-6">
              {magicLinkSent && (
                <div className="p-4 bg-primary-50 border border-primary-200 rounded-lg">
                  <p className="text-sm text-primary-800">
                    Đã gửi liên kết đến email của bạn. Vui lòng kiểm tra hộp thư (kể cả thư rác/spam).
                  </p>
                  <p className="text-xs text-primary-600 mt-1">
                    Liên kết sẽ hết hạn sau 15 phút.
                  </p>
                </div>
              )}

              {import.meta.env.DEV && devLink && (
                <button
                  type="button"
                  onClick={async () => {
                    const url = new URL(devLink);
                    const token = url.searchParams.get('token');
                    if (token) {
                      setLoading(true);
                      try {
                        setSuccess('Đang xác thực...');
                        const res = await verifyMagicLink(token, 'login');
                        if (res && (res as any).requires_2fa) {
                          setTempToken2fa((res as any).temp_token);
                          setStep('2fa');
                          setSuccess(null);
                          return;
                        }
                        setSuccess('Đăng nhập thành công! Đang chuyển hướng...');
                        setTimeout(() => navigate('/dashboard'), 600);
                      } catch (err: any) {
                        setError(err.response?.data?.error?.message || 'Lỗi xác thực token');
                      } finally {
                        setLoading(false);
                      }
                    }
                  }}
                  className="w-full text-left p-3 bg-warning-50 border border-warning-200 rounded-lg text-sm text-warning-800 hover:bg-warning-100 transition-colors"
                >
                  <strong>[DEV]</strong> Nhấn vào đây để xác thực và đăng nhập ngay.
                </button>
              )}

              <div>
                <label htmlFor="token" className="label">
                  Mã xác thực (tùy chọn - tự động điền nếu click từ email)
                </label>
                <input
                  {...registerVerify('token')}
                  id="token"
                  type="text"
                  className={`input ${verifyErrors.token ? 'input-error' : ''}`}
                  placeholder="Nhập token từ email..."
                  disabled={loading}
                />
                {verifyErrors.token && (
                  <p className="mt-1 text-sm text-danger-600">{verifyErrors.token.message}</p>
                )}
              </div>

              <button
                type="submit"
                className="btn-primary w-full"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Đang xác thực...
                  </>
                ) : (
                  'Xác thực và đăng nhập'
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setStep('email');
                  setMagicLinkSent(false);
                  setError(null);
                  setSuccess(null);
                }}
                className="btn-ghost w-full"
                disabled={loading}
              >
                Quay lại nhập email
              </button>
            </form>
          )}

          <div className="mt-6 text-center text-sm text-gray-500">
            <p>Bằng cách đăng nhập, bạn đồng ý với</p>
            <div className="flex justify-center gap-4 mt-1">
              <a href="#" className="text-primary-600 hover:text-primary-700">Điều khoản sử dụng</a>
              <a href="#" className="text-primary-600 hover:text-primary-700">Chính sách bảo mật</a>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center text-sm text-gray-500">
          <p>Cần hỗ trợ? <a href="#" className="text-primary-600 hover:text-primary-700">Liên hệ chúng tôi</a></p>
        </div>
      </div>
    </div>
  );
}